/**
 * WeeklyPlanView — Monday-Friday grid overview of Daily Plans.
 *
 * - Fetches plans between the week's Mon and Sun (extra Sat/Sun rendered smaller).
 * - Click a day → opens the plan for editing (admin) or just scrolls it into focus.
 * - Prev / Next / Today navigation at the top.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CalendarDays, Loader2, Plus, Edit3, Users, Clock, X, Trash2 } from "lucide-react";
import RichContent from "./RichContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_LABELS = {
  ro: ["Luni", "Marți", "Miercuri", "Joi", "Vineri", "Sâmbătă", "Duminică"],
  no: ["Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag", "Søndag"],
  en: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
};

function isoDate(d) { return d.toISOString().slice(0, 10); }
function fromIso(s) { return new Date(s + "T00:00:00"); }

/** Return Monday of the week containing `d` (locale-independent — treats Monday as start). */
function mondayOf(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const dow = x.getDay(); // 0..6 (Sun..Sat)
  const diff = (dow === 0 ? -6 : 1 - dow);
  x.setDate(x.getDate() + diff);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function isHtml(s) {
  return !!s && /<\/?[a-z][\s\S]*>/i.test(s);
}

export default function WeeklyPlanView({ authHeader, isAdmin, onEdit, onNew, lang = "no" }) {
  const [monday, setMonday] = useState(() => mondayOf(new Date()));
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(true);
  const [meetings, setMeetings] = useState([]);
  const [projects, setProjects] = useState([]);
  const [mtgForm, setMtgForm] = useState(null); // { meeting_date, title, time, project_id, comment } | null
  const [savingMtg, setSavingMtg] = useState(false);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(monday, i)), [monday]);
  const startIso = isoDate(days[0]);
  const endIso   = isoDate(days[6]);

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        // The backend endpoint doesn't filter by range yet — fetch a big window and slice.
        const { data } = await axios.get(`${API}/doserne/daily-plans?limit=200`, authHeader());
        const inWeek = (data.plans || []).filter((p) => {
          const d = p.plan_date;
          return d >= startIso && d <= endIso;
        });
        setPlans(inWeek);
      } catch { toast.error("Kunne ikke laste"); }
      setBusy(false);
    })();
  }, [startIso, endIso, authHeader]);

  const loadMeetings = async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/meetings?start=${startIso}&end=${endIso}`, authHeader());
      setMeetings(data.meetings || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadMeetings(); }, [startIso, endIso, authHeader]); // eslint-disable-line
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/doserne/projects?limit=300`, authHeader());
        setProjects(data.projects || data || []);
      } catch { /* ignore */ }
    })();
  }, [authHeader]);

  const openMtgForm = (iso) => setMtgForm({ meeting_date: iso || startIso, title: "", time: "", project_id: "", comment: "" });
  const saveMeeting = async () => {
    if (!mtgForm?.title.trim() || !mtgForm?.meeting_date) { toast.error("Titlu și dată sunt obligatorii"); return; }
    setSavingMtg(true);
    try {
      const proj = projects.find((p) => p.id === mtgForm.project_id);
      await axios.post(`${API}/doserne/meetings`, {
        title: mtgForm.title.trim(),
        meeting_date: mtgForm.meeting_date,
        time: mtgForm.time || "",
        project_id: mtgForm.project_id || "",
        project: proj ? (proj.name || proj.address || "") : "",
        comment: mtgForm.comment || "",
      }, authHeader());
      toast.success("Ședință adăugată");
      setMtgForm(null);
      loadMeetings();
    } catch { toast.error("Nu am putut salva ședința"); }
    setSavingMtg(false);
  };
  const deleteMeeting = async (id) => {
    try { await axios.delete(`${API}/doserne/meetings/${id}`, authHeader()); loadMeetings(); }
    catch { toast.error("Nu am putut șterge"); }
  };

  const meetingsByDate = useMemo(() => {
    const m = {};
    meetings.forEach((x) => { (m[x.meeting_date] = m[x.meeting_date] || []).push(x); });
    return m;
  }, [meetings]);

  const plansByDate = useMemo(() => {
    const m = {};
    plans.forEach((p) => { (m[p.plan_date] = m[p.plan_date] || []).push(p); });
    return m;
  }, [plans]);

  const goPrev  = () => setMonday(addDays(monday, -7));
  const goNext  = () => setMonday(addDays(monday, +7));
  const goToday = () => setMonday(mondayOf(new Date()));

  const weekLabelLocale = lang === "ro" ? "ro-RO" : lang === "en" ? "en-GB" : "no-NO";
  const weekRange = `${days[0].toLocaleDateString(weekLabelLocale, { day: "numeric", month: "short" })} — ${days[6].toLocaleDateString(weekLabelLocale, { day: "numeric", month: "short" })}`;

  const todayIso = isoDate(new Date());
  const labels = DAY_LABELS[lang] || DAY_LABELS.no;

  return (
    <div data-testid="weekly-plan-view">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4 bg-white border border-slate-200 rounded-xl px-3 py-2">
        <div className="flex items-center gap-2">
          <button onClick={goPrev} className="p-2 rounded-lg text-slate-600 hover:bg-slate-100" title="Săptămâna anterioară" data-testid="week-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={goToday} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100" data-testid="week-today">
            <CalendarDays className="w-3.5 h-3.5" /> Azi
          </button>
          <button onClick={goNext} className="p-2 rounded-lg text-slate-600 hover:bg-slate-100" title="Săptămâna următoare" data-testid="week-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="text-sm font-black text-slate-900 uppercase tracking-wider">{weekRange}</div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={() => openMtgForm(startIso)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest text-indigo-700 border border-indigo-200 bg-indigo-50 hover:bg-indigo-100"
              data-testid="add-meeting-btn"
            >
              <Users className="w-3.5 h-3.5" /> Ședință
            </button>
          )}
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
            {plans.length} {plans.length === 1 ? "plan" : "planuri"}
          </div>
        </div>
      </div>

      {mtgForm && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="meeting-form-modal" onClick={() => setMtgForm(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-indigo-50">
              <div className="flex items-center gap-2 text-indigo-800 font-black uppercase tracking-wider text-sm">
                <Users className="w-4 h-4" /> Ședință nouă
              </div>
              <button onClick={() => setMtgForm(null)} className="p-1 rounded-md text-slate-400 hover:bg-white" data-testid="meeting-form-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Titlu</label>
                <input value={mtgForm.title} onChange={(e) => setMtgForm({ ...mtgForm, title: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:border-indigo-400"
                  placeholder="ex. Befaring Tollbugata" data-testid="meeting-title-input" autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Dată</label>
                  <input type="date" value={mtgForm.meeting_date} onChange={(e) => setMtgForm({ ...mtgForm, meeting_date: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:border-indigo-400" data-testid="meeting-date-input" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Oră</label>
                  <input type="time" value={mtgForm.time} onChange={(e) => setMtgForm({ ...mtgForm, time: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:border-indigo-400" data-testid="meeting-time-input" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Proiect</label>
                <select value={mtgForm.project_id} onChange={(e) => setMtgForm({ ...mtgForm, project_id: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white focus:outline-none focus:border-indigo-400" data-testid="meeting-project-select">
                  <option value="">— Fără proiect —</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name || p.address || p.id}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Comentariu</label>
                <textarea value={mtgForm.comment} onChange={(e) => setMtgForm({ ...mtgForm, comment: e.target.value })} rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm resize-none focus:outline-none focus:border-indigo-400"
                  placeholder="Detalii, participanți, locație…" data-testid="meeting-comment-input" />
              </div>
            </div>
            <div className="flex gap-2 px-4 py-3 border-t border-slate-100">
              <button onClick={() => setMtgForm(null)} className="flex-1 py-2 rounded-lg text-sm font-bold text-slate-600 border border-slate-200 hover:bg-slate-50">Anulează</button>
              <button onClick={saveMeeting} disabled={savingMtg}
                className="flex-1 py-2 rounded-lg text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 inline-flex items-center justify-center gap-1"
                data-testid="meeting-save-btn">
                {savingMtg ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvează"}
              </button>
            </div>
          </div>
        </div>
      )}

      {busy ? (
        <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <>
          {/* Mon-Fri: 5-column grid on desktop */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-3">
            {days.slice(0, 5).map((d, i) => (
              <DayCard
                key={i} day={d} label={labels[i]}
                plans={plansByDate[isoDate(d)] || []}
                meetings={meetingsByDate[isoDate(d)] || []}
                isToday={isoDate(d) === todayIso}
                isAdmin={isAdmin} onEdit={onEdit} onNew={onNew}
                onAddMeeting={openMtgForm} onDeleteMeeting={deleteMeeting}
                weekend={false}
              />
            ))}
          </div>

          {/* Sat + Sun: half-width row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 opacity-95">
            {days.slice(5).map((d, i) => (
              <DayCard
                key={i + 5} day={d} label={labels[i + 5]}
                plans={plansByDate[isoDate(d)] || []}
                meetings={meetingsByDate[isoDate(d)] || []}
                isToday={isoDate(d) === todayIso}
                isAdmin={isAdmin} onEdit={onEdit} onNew={onNew}
                onAddMeeting={openMtgForm} onDeleteMeeting={deleteMeeting}
                weekend
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function DayCard({ day, label, plans, meetings = [], isToday, isAdmin, onEdit, onNew, onAddMeeting, onDeleteMeeting, weekend }) {
  const iso = isoDate(day);
  const hasPlan = plans.length > 0;
  const hasMeetings = meetings.length > 0;
  const dayNum = day.getDate();

  return (
    <div
      className={`relative rounded-xl border-2 transition min-h-[140px] flex flex-col ${
        isToday ? "border-emerald-400 shadow-sm" : "border-slate-200"
      } ${weekend ? "bg-slate-50/60" : "bg-white"}`}
      data-testid={`day-card-${iso}`}
    >
      {/* Header */}
      <div className={`flex items-center justify-between px-3 py-2 border-b ${isToday ? "bg-emerald-50 border-emerald-200" : "border-slate-100"} rounded-t-xl`}>
        <div>
          <div className={`text-[10px] uppercase tracking-widest font-black ${isToday ? "text-emerald-700" : "text-slate-500"}`}>{label}</div>
          <div className="text-lg font-black text-slate-900 leading-none">{dayNum}</div>
        </div>
        <div className="flex items-center gap-1">
          {isAdmin && (
            <button
              onClick={() => onAddMeeting?.(iso)}
              className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-100"
              title="Adaugă ședință în această zi"
              data-testid={`day-add-meeting-${iso}`}
            >
              <Users className="w-4 h-4" />
            </button>
          )}
          {isAdmin && !hasPlan && (
            <button
              onClick={() => onNew?.(iso)}
              className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-100"
              title="Adaugă plan pentru această zi"
              data-testid={`day-new-${iso}`}
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 p-3 space-y-2">
        {hasMeetings && (
          <div className="space-y-1.5">
            {meetings.map((mtg) => (
              <div
                key={mtg.id}
                className="group rounded-lg border border-indigo-200 bg-indigo-50/70 px-2 py-1.5"
                data-testid={`day-meeting-${mtg.id}`}
              >
                <div className="flex items-start justify-between gap-1">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1 text-[11px] font-black text-indigo-900 leading-tight">
                      <Users className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{mtg.title}</span>
                    </div>
                    {(mtg.time || mtg.project) && (
                      <div className="flex items-center gap-1 text-[10px] text-indigo-700/80 mt-0.5">
                        {mtg.time && <span className="inline-flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{mtg.time}</span>}
                        {mtg.project && <span className="truncate">· {mtg.project}</span>}
                      </div>
                    )}
                    {mtg.comment && <div className="text-[10px] text-indigo-800/70 mt-0.5 line-clamp-2">{mtg.comment}</div>}
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => onDeleteMeeting?.(mtg.id)}
                      className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-indigo-400 hover:text-red-500 flex-shrink-0"
                      title="Șterge ședința"
                      data-testid={`day-meeting-del-${mtg.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {!hasPlan ? (
          !hasMeetings && <div className="text-[11px] text-slate-400 italic text-center pt-4">Niciun plan</div>
        ) : (
          <div className="space-y-2">
            {plans.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => isAdmin && onEdit?.(p)}
                className={`w-full text-left rounded-lg p-2 border transition ${isAdmin ? "hover:border-emerald-400 hover:shadow-sm cursor-pointer" : "cursor-default"} border-slate-200`}
                style={{ backgroundColor: p.background_color || "#ffffff" }}
                data-testid={`day-plan-${p.id}`}
                title={isAdmin ? "Apasă pentru editare" : ""}
              >
                <div className="flex items-start justify-between gap-1">
                  <div className="text-xs font-black text-slate-900 leading-tight line-clamp-2">{p.title}</div>
                  {isAdmin && <Edit3 className="w-3 h-3 text-slate-300 flex-shrink-0 mt-0.5" />}
                </div>
                {p.body && (
                  <div className="text-[10px] text-slate-600 mt-1 line-clamp-4 leading-snug overflow-hidden">
                    {isHtml(p.body) ? (
                      <RichContent html={p.body} className="[&_h1]:!text-xs [&_h2]:!text-xs [&_p]:!text-[10px] [&_ul]:!text-[10px] [&_ol]:!text-[10px] [&_li]:!my-0 [&_h1]:!my-0 [&_h2]:!my-0 [&_p]:!my-0" />
                    ) : (
                      <div className="whitespace-pre-wrap">{p.body.slice(0, 200)}{p.body.length > 200 ? "…" : ""}</div>
                    )}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
